import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { projectService, type ProjectInvoice } from '@services/projectService';
import { normalizeInvoice } from '../normalizers/invoiceNormalizers';
import { CACHE_DURATION } from '../utils/cache';
import type { RootState } from '../index';

interface InvoiceState {
  invoices: ProjectInvoice[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: InvoiceState = {
  invoices: [],
  isLoading: false,
  isSaving: false,
  error: null,
  lastFetched: null,
};

export const fetchInvoices = createAsyncThunk(
  'invoices/fetchAll',
  async (options: { force?: boolean } | undefined, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { invoices, lastFetched } = state.invoices;

    if (
      !options?.force &&
      invoices.length > 0 &&
      lastFetched &&
      Date.now() - lastFetched < CACHE_DURATION
    ) {
      return null;
    }

    try {
      const fetchedInvoices = await projectService.getAllInvoices();

      return fetchedInvoices
        .map(invoice => normalizeInvoice(invoice as unknown as Record<string, unknown>))
        .sort(
          (left, right) =>
            Date.parse(right.updatedAt || right.createdAt) -
            Date.parse(left.updatedAt || left.createdAt)
        );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load invoices';
      return rejectWithValue(msg);
    }
  }
);

export const createInvoice = createAsyncThunk(
  'invoices/create',
  async (
    { projectId, data }: { projectId: string; data: Omit<ProjectInvoice, 'id'> },
    { getState, rejectWithValue }
  ) => {
    try {
      const created = await projectService.createInvoice(projectId, data);
      const state = getState() as RootState;
      const project = state.projects.projects.find(entry => entry.id === projectId);

      return normalizeInvoice(created as unknown as Record<string, unknown>, {
        id: projectId,
        name: project?.name ?? data.projectName,
        contactName: project?.contactName ?? data.contactName,
        contactEmail: project?.contactEmail ?? data.contactEmail,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create invoice';
      return rejectWithValue(msg);
    }
  }
);

export const updateInvoice = createAsyncThunk(
  'invoices/update',
  async (
    {
      projectId,
      invoiceId,
      data,
    }: { projectId: string; invoiceId: string; data: Partial<ProjectInvoice> },
    { getState, rejectWithValue }
  ) => {
    try {
      const updated = await projectService.updateInvoice(projectId, invoiceId, data);
      const state = getState() as RootState;
      const project = state.projects.projects.find(entry => entry.id === projectId);

      return normalizeInvoice(updated as unknown as Record<string, unknown>, {
        id: projectId,
        name: project?.name ?? data.projectName ?? '',
        contactName: project?.contactName ?? data.contactName,
        contactEmail: project?.contactEmail ?? data.contactEmail,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update invoice';
      return rejectWithValue(msg);
    }
  }
);

export const deleteInvoice = createAsyncThunk(
  'invoices/delete',
  async (
    { projectId, invoiceId }: { projectId: string; invoiceId: string },
    { rejectWithValue }
  ) => {
    try {
      await projectService.deleteInvoice(projectId, invoiceId);
      return invoiceId;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete invoice';
      return rejectWithValue(msg);
    }
  }
);

const invoiceSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchInvoices.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload !== null) {
          state.invoices = action.payload;
          state.lastFetched = Date.now();
        }
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createInvoice.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.isSaving = false;
        state.invoices.unshift(action.payload);
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      .addCase(updateInvoice.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        state.isSaving = false;
        const index = state.invoices.findIndex(invoice => invoice.id === action.payload.id);
        if (index !== -1) state.invoices[index] = action.payload;
      })
      .addCase(updateInvoice.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      .addCase(deleteInvoice.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.isSaving = false;
        state.invoices = state.invoices.filter(invoice => invoice.id !== action.payload);
      })
      .addCase(deleteInvoice.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      });
  },
});

export const selectInvoices = (state: RootState) => state.invoices.invoices;
export const selectInvoicesLoading = (state: RootState) => state.invoices.isLoading;
export const selectInvoicesSaving = (state: RootState) => state.invoices.isSaving;
export const selectInvoicesError = (state: RootState) => state.invoices.error;

export default invoiceSlice.reducer;
